/**
 * VersionsPanel — the Versions panel of §11 tab 2 (contracts-plan.md §11
 * tab 2, §4.5, §5.4, §5.5, §6.8 G9, R32, R34; §13.8 WP-74).
 *
 * SELF-CONTAINED, per session 4's orchestrator decision: a fixed-props
 * component (no children, no render props, no callbacks) that fetches its
 * own data from `negotiationId` and owns its own state — the shape WP-71's
 * FeesPanel and WP-70's NotesPanel already take. It is mounted into WP-34's
 * NegotiationModal by the sidecar (versionsPanel.mount.md); this file is
 * never imported there directly.
 *
 * NO ROUTING (FACTS 11, deliberate): WP-34's studies.test.tsx renders
 * StudiesTab with no Router in the tree, so nothing here imports from
 * react-router-dom — a Link/useNavigate/useSearchParams would throw the
 * moment the mount step drops this panel into NegotiationModal.
 *
 * Wraps its own content in a local <ToastProvider>: the fixed test tree
 * (ThemeProvider -> AuthContext.Provider -> ContractsProvider ->
 * <VersionsPanel />) carries none, and useToast throws without one
 * (frontend/src/ui/Toast.tsx) — the same self-sufficiency StudiesTab,
 * CardView and WorkspaceTab already give themselves at their own top level.
 */
import { useCallback, useEffect, useState } from 'react';
import { FileStack, AlertCircle } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import Modal from '../../../ui/Modal';
import { ToastProvider, useToast } from '../../../ui/Toast';
import { downloadVersion } from '../../api/workspace';
import type { ContractsVersionDirection } from '../../api/workspace';
import {
  listVersionsWithInventory, getVersionDiff, getContractRedlineDiff, markVersionExecuted, supersedeVersion,
  describeVersionsApiError,
} from '../../api/versions';
import type {
  VersionWithInventory, VersionDiffResult, RedlineDiffSummary,
} from '../../api/versions';
import { VersionList } from './VersionList';
import { DiffView } from './DiffView';

export interface VersionsPanelProps {
  negotiationId: string;
  className?: string;
  readOnly?: boolean;
}

function downloadBlob(bytes: ArrayBuffer, contentType: string, filename: string) {
  const blob = new Blob([bytes], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface SupersedeFormState {
  versionId: string;
  direction: ContractsVersionDirection;
  round: string;
  versionDate: string;
}

function VersionsPanelInner({ negotiationId, className, readOnly = false }: VersionsPanelProps) {
  const { toast } = useToast();

  const [versions, setVersions] = useState<VersionWithInventory[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [diffResult, setDiffResult] = useState<VersionDiffResult | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [executingId, setExecutingId] = useState<string | null>(null);

  const [supersedeForm, setSupersedeForm] = useState<SupersedeFormState | null>(null);
  const [supersedeSaving, setSupersedeSaving] = useState(false);

  const [redlineLoading, setRedlineLoading] = useState(false);
  const [redlineError, setRedlineError] = useState<string | null>(null);
  const [redlineSummary, setRedlineSummary] = useState<RedlineDiffSummary | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const rows = await listVersionsWithInventory(negotiationId);
      setVersions(rows);
    } catch (err) {
      setLoadError(describeVersionsApiError('GET', `/api/contracts/negotiations/${negotiationId}/versions`, err));
    }
  }, [negotiationId]);

  useEffect(() => {
    load();
  }, [load]);

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleCompare() {
    const [idA, idB] = [...selected];
    if (!idA || !idB) return;
    setError(null);
    setDiffLoading(true);
    setDiffResult(null);
    setRedlineSummary(null);
    setRedlineError(null);
    try {
      const result = await getVersionDiff(idA, idB);
      setDiffResult(result);
    } catch (err) {
      setError(describeVersionsApiError('GET', `/api/contracts/versions/${idA}/diff/${idB}`, err));
    } finally {
      setDiffLoading(false);
    }
  }

  async function handleDownload(id: string) {
    setError(null);
    try {
      const download = await downloadVersion(id);
      window.open(download.url, '_blank', 'noopener');
    } catch (err) {
      setError(describeVersionsApiError('GET', `/api/contracts/versions/${id}/download`, err));
    }
  }

  async function handleMarkExecuted(id: string) {
    setError(null);
    setExecutingId(id);
    try {
      await markVersionExecuted(id);
      toast('Version marked executed.', { type: 'success' });
      await load();
    } catch (err) {
      const message = describeVersionsApiError('POST', `/api/contracts/versions/${id}/executed`, err);
      setError(message);
      toast(message, { type: 'error' });
    } finally {
      setExecutingId(null);
    }
  }

  function openSupersede(id: string) {
    const version = versions?.find((v) => v.id === id);
    setSupersedeForm({
      versionId: id,
      direction: version?.direction ?? 'received',
      round: version?.round != null ? String(version.round) : '',
      versionDate: version?.version_date ?? '',
    });
  }

  async function handleSupersedeSubmit() {
    if (!supersedeForm) return;
    setSupersedeSaving(true);
    setError(null);
    try {
      await supersedeVersion(supersedeForm.versionId, {
        direction: supersedeForm.direction,
        round: supersedeForm.round === '' ? null : Number(supersedeForm.round),
        versionDate: supersedeForm.versionDate || undefined,
      });
      toast('Superseding version recorded.', { type: 'success' });
      setSupersedeForm(null);
      await load();
    } catch (err) {
      const message = describeVersionsApiError('POST', `/api/contracts/versions/${supersedeForm.versionId}/supersede`, err);
      setError(message);
      toast(message, { type: 'error' });
    } finally {
      setSupersedeSaving(false);
    }
  }

  async function handleDownloadRedline() {
    if (!diffResult) return;
    setRedlineLoading(true);
    setRedlineError(null);
    try {
      const redline = await getContractRedlineDiff(diffResult.a.id, diffResult.b.id);
      setRedlineSummary(redline.summary);
      downloadBlob(redline.bytes, redline.contentType, redline.filename ?? 'redline.docx');
    } catch (err) {
      setRedlineError(describeVersionsApiError('GET', '/api/contracts/diff', err));
    } finally {
      setRedlineLoading(false);
    }
  }

  const compareDisabled = selected.size !== 2 || diffLoading;

  return (
    <div data-testid="ver-panel" className={className}>
      <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Versions</h4>
      {(error || loadError) && (
        <div
          data-testid="ver-error"
          className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
        >
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error ?? loadError}</span>
        </div>
      )}

      {versions === null ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading versions…</p>
      ) : !Array.isArray(versions) || versions.length === 0 ? (
        <EmptyState icon={FileStack} title="No versions yet" hint="Documents and versions will appear here once approved (§4.5)." variant="card" />
      ) : (
        <div className="space-y-4">
          <VersionList
            versions={versions}
            selected={selected}
            onToggleSelect={toggleSelect}
            readOnly={readOnly}
            onDownload={handleDownload}
            onMarkExecuted={handleMarkExecuted}
            onOpenSupersede={openSupersede}
            executingId={executingId}
          />

          {!readOnly && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="ver-compare"
                onClick={handleCompare}
                disabled={compareDisabled}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                {diffLoading ? 'Comparing…' : 'Compare'}
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400">{selected.size} selected</span>
            </div>
          )}

          {diffResult && (
            <DiffView
              result={diffResult}
              onDownloadRedline={handleDownloadRedline}
              redlineLoading={redlineLoading}
              redlineError={redlineError}
              redlineSummary={redlineSummary}
            />
          )}
        </div>
      )}

      {supersedeForm && (
        <Modal isOpen onClose={() => setSupersedeForm(null)} title="Supersede version" maxWidth="max-w-md">
          <div className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              A version's bytes are never edited in place (§4.5, R32) — this inserts a new row correcting direction, round or date, referencing the one it replaces.
            </p>
            <div>
              <label htmlFor="ver-supersede-direction" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Direction</label>
              <select
                id="ver-supersede-direction"
                data-testid="ver-supersede-direction"
                value={supersedeForm.direction}
                onChange={(e) => setSupersedeForm((prev) => (prev ? { ...prev, direction: e.target.value as ContractsVersionDirection } : prev))}
                className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="received">Received</option>
                <option value="sent">Sent</option>
                <option value="internal">Internal</option>
              </select>
            </div>
            <div>
              <label htmlFor="ver-supersede-round" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Round</label>
              <input
                id="ver-supersede-round"
                data-testid="ver-supersede-round"
                type="number"
                value={supersedeForm.round}
                onChange={(e) => setSupersedeForm((prev) => (prev ? { ...prev, round: e.target.value } : prev))}
                className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <div>
              <label htmlFor="ver-supersede-date" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Version date</label>
              <input
                id="ver-supersede-date"
                data-testid="ver-supersede-date"
                type="date"
                value={supersedeForm.versionDate}
                onChange={(e) => setSupersedeForm((prev) => (prev ? { ...prev, versionDate: e.target.value } : prev))}
                className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setSupersedeForm(null)}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="ver-supersede-submit"
                onClick={handleSupersedeSubmit}
                disabled={supersedeSaving}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                {supersedeSaving ? 'Saving…' : 'Supersede'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function VersionsPanel(props: VersionsPanelProps): JSX.Element {
  return (
    <ToastProvider>
      <VersionsPanelInner {...props} />
    </ToastProvider>
  );
}
